import { createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider } from "@sentara-health/optimahealth_web";

import {
  AVMED_THEME,
  SHP_THEME,
  BRAND_CONFIG,
  Theme,
} from "../../utils/constants/BrandConfig";
import { LocaleType, BrandType } from "../../utils/models/CommonTypes";

export enum Brands {
  AVMED = "AVMED",
  SENTARA_HEALTH_PLANS = "SENTARAHEALTHPLANS",
}

type BrandProviderType = {
  brand: Brands;
  brandConfig: BrandType;
};

interface Props {
  children?: React.ReactNode;
}

export const BrandingContext = createContext<BrandProviderType>({
  brand: Brands.SENTARA_HEALTH_PLANS,
  brandConfig: {},
} as any);

const isBrand = (brand: Brands) => {
  const hostName = window.location.hostname;
  return BRAND_CONFIG[brand].hostNames?.includes(hostName);
};

interface BrandSetting {
  brand: Brands;
  theme: Theme;
  config: BrandType;
  locale: LocaleType;
}

const brandMap: Record<string, BrandSetting> = {
  AVMED: {
    brand: Brands.AVMED,
    theme: AVMED_THEME,
    config: BRAND_CONFIG[Brands.AVMED],
    locale: LocaleType.AVMED,
  },
  SENTARAHEALTHPLANS: {
    brand: Brands.SENTARA_HEALTH_PLANS,
    theme: SHP_THEME,
    config: BRAND_CONFIG[Brands.SENTARA_HEALTH_PLANS],
    locale: LocaleType.SENTARA,
  },
};

const normalizeBrandKey = (brandKey: string | undefined): string => {
  const key = brandKey?.toLowerCase() || "";
  const keyAliases: Record<string, string> = {
    shp: "sentarahealthplans",
    sentara: "sentarahealthplans",
    sentara_health_plans: "sentarahealthplans",
    avm: "avmed",
    av: "avmed",
  };
  return (keyAliases[key] || key).toUpperCase();
};

const getBrandSettings = (): BrandSetting => {
  const environment = process.env.NODE_ENV;

  if (environment === "development") {
    const envBrand = normalizeBrandKey(process.env.REACT_APP_BRAND);

    if (envBrand && Object.values(Brands).includes(envBrand as Brands)) {
      const settings = brandMap[envBrand];
      if (settings) {
        return settings;
      }
    }
  }

  const brand = isBrand(Brands.AVMED)
    ? Brands.AVMED
    : Brands.SENTARA_HEALTH_PLANS;
  return brandMap[brand];
};

export const BrandingProvider = ({ children }: Props) => {
  const { i18n } = useTranslation();
  const { theme, brand, locale, config: brandConfig } = getBrandSettings();
  i18n.changeLanguage(locale);

  const value = useMemo(
    () => ({
      brandConfig,
      brand,
    }),
    [brand, brandConfig]
  );

  const themeValue = useMemo(
    () => ({
      theme,
    }),
    [theme]
  );

  console.log("Brand Config Name:", brandConfig.brandName);

  return (
    <BrandingContext.Provider value={value}>
      <ThemeProvider theme={themeValue.theme}>{children}</ThemeProvider>
    </BrandingContext.Provider>
  );
};

export const useBranding = () => {
  return useContext(BrandingContext);
};
